import type { ComponentType, ReactNode } from 'react'
import { createContext, use, useEffect, useMemo, useState } from 'react'
import type { ComplexityEntry } from '@/components/ui/complexity-analysis'
import type { TestType } from '@/lib/constants/test-types'
import { DEFAULT_TEST_TYPE, isValidTestType } from '@/lib/constants/test-types'
import type { ProblemTopic } from '@/lib/problems/problem-topics'
import { type RouterOutputs, trpc } from '@/lib/trpc.js'
import type { ClassOperation, ProblemTestCase } from '@/lib/types/test-cases'
import { parseJsonValue } from './test-cases-pane/utils'

// Re-export for backwards compatibility (other files import from here)
export type { ClassOperation, ComplexityEntry as SolutionComplexity }

// =============================================================================
// Types
// =============================================================================

export interface ApproachData {
  index: number
  explanation: ComponentType | null
  solutions: {
    python: string | null
    js: string | null
  }
  complexities?: {
    python?: ComplexityEntry[]
    js?: ComplexityEntry[]
  }
}

/**
 * Function-based example (inputs/output)
 */
export interface FunctionExample {
  index: number
  inputs: Record<string, string>
  output: string
  /**
   * Values of in-place-modified params after the call, keyed by param name.
   * For void problems this IS the answer — `output` is empty and the card
   * would otherwise show a blank Output. Same name as the DB column and the
   * harness field so the term stays greppable end to end.
   *
   * Values are JSON *text*, ready to render as-is (`[3,2,1]`) — never raw
   * values. Producers own the encoding, and the two differ: the DB column is
   * parsed jsonb and gets stringified here, while the admin form already
   * holds the text the admin typed. Handing this a parsed value renders
   * `[object Object]`; handing it pre-quoted text renders the quotes.
   */
  expectedInputsAfter?: Record<string, string>
  imageFilename?: string
  inputDisplayOverride?: string
  outputDisplayOverride?: string
}

/**
 * Class-based example (constructorArgs/operations)
 */
export interface ClassExample {
  index: number
  constructorArgs?: string
  operations: ClassOperation[]
  intent?: string
  imageFilename?: string
  inputDisplayOverride?: string
  outputDisplayOverride?: string
}

/**
 * Raw example (admin-authored display text + language-keyed scripts)
 */
export interface RawExample {
  index: number
  display: string
  scripts?: Record<string, string>
}

/**
 * Example from MDX frontmatter - can be function-based, class-based, or raw
 */
export type Example = FunctionExample | ClassExample | RawExample

/**
 * Type guard: Check if example is function-based (has output, not class-based)
 */
export function isFunctionExample(
  example: Example,
): example is FunctionExample {
  return 'output' in example && !('operations' in example)
}

/**
 * Type guard: Check if example is class-based (has operations property)
 */
export function isClassExample(example: Example): example is ClassExample {
  return 'operations' in example && Array.isArray(example.operations)
}

/**
 * Type guard: Check if example is raw (has display property)
 */
export function isRawExample(example: Example): example is RawExample {
  return 'display' in example
}

/**
 * Test configuration from MDX frontmatter - defines function/class signature and comparison mode
 */
export interface TestConfig {
  // Test type - 'function' (default) or 'class'
  testType?: TestType

  // Function-based fields:
  functionName?: string // Optional - when not set, function name is derived from problem slug
  inputParameters?: Array<{
    name: string
    type: string
    userFacing?: boolean // Defaults to true; set false for test-only params (used by custom_setup but not user function)
  }>
  outputType?: string

  // Class-based fields:
  className?: string
  constructorParameters?: Array<{ name: string; type: string }>

  // Shared:
  comparisonMode?: string // 'exact' | 'set_equality' | 'sorted_equality' | etc.

  // Starter code stubs (language-keyed, e.g. { python: "def foo(x):\n    pass\n" })
  starterCode?: Record<string, string>

  // Raw test type: scripts from example test cases (language-keyed per case)
  rawExampleScripts?: Array<Record<string, string>>
}

export interface EditorialApproachMeta {
  id: string
  name: string | null
  solutionType?: string | null
  difficulty?: string | null
  strategies?: string[]
  code?: string | null
  complexities?: ComplexityEntry[]
}

export interface EditorialData {
  markdown: string | null
  approaches: EditorialApproachMeta[]
  format: 'new' | 'legacy'
}

/** Load state of one independently-fetched problem section. */
export type SectionState =
  | { status: 'loading' }
  | { status: 'ready' }
  | { status: 'error'; message: string }

export function deriveQuerySectionState(
  problemSlug: string | null,
  isLoading: boolean,
  error: { message: string } | null | undefined,
  label: string,
): SectionState {
  if (!problemSlug) return { status: 'ready' }
  if (error)
    return {
      status: 'error',
      message: `Failed to load ${label}: ${error.message}`,
    }
  if (isLoading) return { status: 'loading' }
  return { status: 'ready' }
}

export interface ProblemData {
  slug: string
  title: string | null
  statement: ComponentType | null
  editorial: EditorialData | null
  examples: Example[]
  constraints: string[]
  followUps: string[]
  testConfig: TestConfig | null
  approaches: ApproachData[]
  testCases: ReadonlyArray<ProblemTestCase>
  topics: ProblemTopic[]
}

interface ProblemContextValue {
  problemSlug: string | null
  data: ProblemData | null
  /**
   * Languages with complete, validated execution artifacts for this problem
   * (LG-247), from problems.getTestConfig. `null` until the test config
   * loads — callers treat null as "gate on global rollout alone".
   */
  availableLanguages: readonly string[] | null
  /** Per-section load state — narrow on `.status` instead of the legacy booleans. */
  sections: {
    statement: SectionState
    editorial: SectionState
    testCases: SectionState
  }
  // Derived legacy fields
  loading: boolean
  error: string | null
  statementLoading: boolean
  editorialLoading: boolean
  testCasesLoading: boolean
}

// =============================================================================
// Vite Glob Imports (moved from individual panes)
// =============================================================================

// Description MDX module type - includes frontmatter exports
interface DescriptionModule {
  default: ComponentType
  frontmatter?: {
    title?: string
    examples?: Example[]
    testConfig?: TestConfig
    constraints?: string[]
    followUps?: string[]
  }
}

// Description MDX files are generated from database via `pnpm generate:mdx`
const descriptionModules = import.meta.glob<DescriptionModule>(
  '../../generated/*/description.mdx',
  { eager: false },
)

/** Set of all valid problem slugs, derived from generated MDX files at build time. */
export const validProblemSlugs: ReadonlySet<string> = new Set(
  Object.keys(descriptionModules).map((path) => {
    const match = path.match(/\/generated\/([^/]+)\/description\.mdx$/)
    return match![1]
  }),
)

// =============================================================================
// Path Helpers
// =============================================================================

function getDescriptionPath(slug: string): string {
  return `../../generated/${slug}/description.mdx`
}

// =============================================================================
// Data Loaders
// =============================================================================

interface DescriptionData {
  component: ComponentType | null
  title: string | null
  constraints: string[]
  followUps: string[]
}

async function loadDescription(slug: string): Promise<DescriptionData> {
  const path = getDescriptionPath(slug)
  const importFn = descriptionModules[path]
  if (!importFn)
    return {
      component: null,
      title: null,
      constraints: [],
      followUps: [],
    }

  try {
    const module = await importFn()
    return {
      component: module.default,
      title: module.frontmatter?.title ?? null,
      constraints: module.frontmatter?.constraints ?? [],
      followUps: module.frontmatter?.followUps ?? [],
    }
  } catch (err) {
    console.error(`Failed to load description for ${slug}:`, err)
    return {
      component: null,
      title: null,
      constraints: [],
      followUps: [],
    }
  }
}

// =============================================================================
// Context
// =============================================================================

const ProblemContext = createContext<ProblemContextValue | null>(null)

export function useProblem(): ProblemContextValue {
  const context = use(ProblemContext)
  if (!context) {
    throw new Error('useProblem must be used within a ProblemProvider')
  }
  return context
}

// Optional: use this when you're not sure if you're inside a provider
export function useProblemOptional(): ProblemContextValue | null {
  return use(ProblemContext)
}

// =============================================================================
// DB → Frontend Mappers
// =============================================================================

// Derived from the router output so the payload shape has exactly one
// definition — a server-side field change surfaces here as a type error
// instead of being silently absorbed by a hand-maintained mirror.
type DbTestConfigData = NonNullable<RouterOutputs['problems']['getTestConfig']>
type DbExampleCase = DbTestConfigData['exampleCases'][number]

export function mapDbTestCasesToProblemTestCases(
  exampleCases: DbExampleCase[],
  inputParameters: Array<{ name: string; type: string }>,
  slug: string,
): ProblemTestCase[] {
  return exampleCases.map((tc, index) => {
    const inputs: Array<{ name: string; example: unknown }> = []
    const args: unknown[] = []

    if (tc.inputs && inputParameters.length > 0) {
      for (const param of inputParameters) {
        const parsed = parseJsonValue(tc.inputs[param.name])
        inputs.push({ name: param.name, example: parsed })
        args.push(parsed)
      }
    } else if (tc.inputs) {
      for (const [name, rawValue] of Object.entries(tc.inputs)) {
        const parsed = parseJsonValue(rawValue)
        inputs.push({ name, example: parsed })
        args.push(parsed)
      }
    }

    // Preserve the historical `null` default for a missing expectedOutput;
    // parseJsonValue would return `undefined` for a nullish value.
    const expected =
      tc.expectedOutput != null ? parseJsonValue(tc.expectedOutput) : null

    return {
      _id: `${slug}-example-${index}`,
      id: tc.id,
      args,
      inputs,
      testCaseIntent: tc.intent ?? '',
      expected,
      expectedInputsAfter: tc.expectedInputsAfter ?? undefined,
    }
  })
}

export function mapDbFunctionExamples(
  data: DbTestConfigData,
): FunctionExample[] {
  if (data.testType === 'class' || data.testType === 'raw') return []
  // Sorted by the explicit `order` marker, matching buildFunctionExamples
  // and the harness generator — the stored array order is not the contract.
  const userFacingParams = (data.inputParameters ?? [])
    .filter((p) => p.userFacing !== false)
    .sort((a, b) => a.order - b.order)
  return data.exampleCases.map((tc, index) => {
    const inputs: Record<string, string> = {}
    if (tc.inputs) {
      for (const param of userFacingParams) {
        const value = tc.inputs[param.name]
        if (value !== undefined) {
          inputs[param.name] = value
        }
      }
    }
    // The jsonb column holds parsed values, so each one is stringified for
    // display. Walking the params (not the record) keeps the order aligned
    // with the inputs above and stops test-only params from leaking out.
    const inputsAfter: Record<string, string> = {}
    const dbInputsAfter = tc.expectedInputsAfter
    if (dbInputsAfter) {
      for (const param of userFacingParams) {
        if (param.name in dbInputsAfter) {
          inputsAfter[param.name] = JSON.stringify(dbInputsAfter[param.name])
        }
      }
    }
    return {
      index: index + 1,
      inputs,
      output: tc.expectedOutput ?? '',
      expectedInputsAfter:
        Object.keys(inputsAfter).length > 0 ? inputsAfter : undefined,
      imageFilename: tc.imageFilename ?? undefined,
      inputDisplayOverride: tc.inputDisplayOverride ?? undefined,
      outputDisplayOverride: tc.outputDisplayOverride ?? undefined,
    }
  })
}

function mapDbClassExamples(data: DbTestConfigData): ClassExample[] {
  if (data.testType !== 'class') return []
  return data.exampleCases.map((tc, index) => ({
    index: index + 1,
    constructorArgs: tc.constructorArgs
      ? JSON.stringify(tc.constructorArgs)
      : '[]',
    operations: (tc.operations ?? []).map((op) => {
      if ('type' in op && op.type === 'comment') {
        return { type: 'comment' as const, text: (op as { text: string }).text }
      }
      const opRecord = op as {
        method: string
        args: unknown[]
        expected: unknown
        expectsError?: boolean
      }
      return {
        method: opRecord.method,
        args: JSON.stringify(opRecord.args),
        expected: JSON.stringify(opRecord.expected),
        expectsError: opRecord.expectsError,
      }
    }) as ClassOperation[],
    intent: tc.intent ?? undefined,
    inputDisplayOverride: tc.inputDisplayOverride ?? undefined,
    outputDisplayOverride: tc.outputDisplayOverride ?? undefined,
    imageFilename: tc.imageFilename ?? undefined,
  }))
}

function mapDbRawExamples(data: DbTestConfigData): RawExample[] {
  if (data.testType !== 'raw') return []
  return data.exampleCases
    .filter(
      (tc) =>
        tc.exampleDisplay?.trim() ||
        (tc.scripts && Object.values(tc.scripts).some((v) => v?.trim())),
    )
    .map((tc, index) => ({
      index: index + 1,
      display: tc.exampleDisplay ?? '',
      scripts:
        tc.scripts && Object.keys(tc.scripts).length > 0
          ? tc.scripts
          : undefined,
    }))
}

// =============================================================================
// Provider
// =============================================================================

interface ProblemProviderProps {
  problemSlug: string | null
  children: ReactNode
}

export function ProblemProvider({
  problemSlug,
  children,
}: ProblemProviderProps) {
  const [data, setData] = useState<ProblemData | null>(null)
  const [statementSection, setStatementSection] = useState<SectionState>({
    status: 'loading',
  })

  // Fetch editorial via tRPC — auth is handled automatically by TRPCProvider
  const editorialQuery = trpc.problems.getEditorial.useQuery(
    { slug: problemSlug! },
    { enabled: !!problemSlug },
  )

  // Fetch test config + example test cases via tRPC
  const testConfigQuery = trpc.problems.getTestConfig.useQuery(
    { slug: problemSlug! },
    { enabled: !!problemSlug },
  )

  useEffect(() => {
    if (!problemSlug) {
      setData(null)
      setStatementSection({ status: 'ready' })
      return
    }

    // Track if this effect is still current (not stale)
    let isCurrent = true

    // Reset states
    setStatementSection({ status: 'loading' })

    // Initialize data structure
    const problemData: ProblemData = {
      slug: problemSlug,
      title: null,
      statement: null,
      editorial: null,
      examples: [],
      constraints: [],
      followUps: [],
      testConfig: null,
      approaches: [],
      testCases: [],
      topics: [],
    }

    // Load non-tRPC data in parallel, updating progressively
    const loadAll = async () => {
      try {
        const statementPromise = loadDescription(problemSlug).then(
          (descriptionData) => {
            if (!isCurrent) return
            problemData.title = descriptionData.title
            problemData.statement = descriptionData.component
            problemData.constraints = descriptionData.constraints
            problemData.followUps = descriptionData.followUps
            setStatementSection({ status: 'ready' })
            setData({ ...problemData })
          },
        )

        await statementPromise

        if (!isCurrent) return
        setData(problemData)
      } catch (err) {
        if (!isCurrent) return
        console.error('Failed to load problem data:', err)
        setStatementSection({
          status: 'error',
          message: `Failed to load problem: ${err}`,
        })
      }
    }

    loadAll()

    // Cleanup: mark this effect as stale when problemSlug changes
    return () => {
      isCurrent = false
    }
  }, [problemSlug])

  // Merge editorial data from tRPC into problem data when it arrives
  const editorialData: EditorialData | null = useMemo(() => {
    if (!editorialQuery.data) return null
    return {
      markdown: editorialQuery.data.markdown,
      approaches: editorialQuery.data.approaches ?? [],
      format: editorialQuery.data.format ?? 'new',
    }
  }, [editorialQuery.data])

  const editorialSection = useMemo(
    () =>
      deriveQuerySectionState(
        problemSlug,
        editorialQuery.isLoading,
        editorialQuery.error,
        'editorial',
      ),
    [problemSlug, editorialQuery.isLoading, editorialQuery.error],
  )
  const testCasesSection = useMemo(
    () =>
      deriveQuerySectionState(
        problemSlug,
        testConfigQuery.isLoading,
        testConfigQuery.error,
        'test config',
      ),
    [problemSlug, testConfigQuery.isLoading, testConfigQuery.error],
  )

  // Derive test config from DB
  const dbTestConfig: TestConfig | null = useMemo(() => {
    if (!testConfigQuery.data) return null
    const d = testConfigQuery.data
    return {
      // Validate the DB value with the existing guard instead of a blind cast:
      // testType is an unconstrained text column, so a bad/typo'd value would
      // otherwise be smuggled through as a valid literal and silently render
      // the wrong Test Cases tab. Degrade to the documented default instead.
      testType: isValidTestType(d.testType) ? d.testType : DEFAULT_TEST_TYPE,
      functionName: d.functionName ?? undefined,
      inputParameters: d.inputParameters?.map((p) => ({
        name: p.name,
        type: p.type,
        userFacing: p.userFacing,
      })),
      outputType: d.outputType ?? undefined,
      className: d.className ?? undefined,
      comparisonMode: d.comparisonMode,
      starterCode: d.starterCode ?? undefined,
      rawExampleScripts:
        d.testType === 'raw'
          ? d.exampleCases.filter((tc) => tc.scripts).map((tc) => tc.scripts!)
          : undefined,
    }
  }, [testConfigQuery.data])

  // Derive function test cases from DB (skip for class-based problems)
  const dbTestCases: ReadonlyArray<ProblemTestCase> = useMemo(() => {
    if (!testConfigQuery.data || !problemSlug) return []
    const d = testConfigQuery.data
    if (d.testType === 'class' || d.testType === 'raw') return []
    return mapDbTestCasesToProblemTestCases(
      d.exampleCases,
      d.inputParameters,
      problemSlug,
    )
  }, [testConfigQuery.data, problemSlug])

  // Derive class examples from DB
  const dbClassExamples: ClassExample[] = useMemo(() => {
    if (!testConfigQuery.data) return []
    return mapDbClassExamples(testConfigQuery.data)
  }, [testConfigQuery.data])

  // Derive function examples from DB
  const dbFunctionExamples: FunctionExample[] = useMemo(() => {
    if (!testConfigQuery.data) return []
    return mapDbFunctionExamples(testConfigQuery.data)
  }, [testConfigQuery.data])

  // Derive raw examples from DB
  const dbRawExamples: RawExample[] = useMemo(() => {
    if (!testConfigQuery.data) return []
    return mapDbRawExamples(testConfigQuery.data)
  }, [testConfigQuery.data])

  // Derive display examples: class examples for class problems, function examples for function problems, raw for raw
  const dbExamples: Example[] = useMemo(() => {
    if (dbRawExamples.length > 0) return dbRawExamples
    if (dbClassExamples.length > 0) return dbClassExamples
    if (dbFunctionExamples.length > 0) return dbFunctionExamples
    return []
  }, [dbRawExamples, dbClassExamples, dbFunctionExamples])

  // Derive topics from DB test config response
  const dbTopics: ProblemTopic[] = useMemo(() => {
    if (!testConfigQuery.data) return []
    return testConfigQuery.data.topics
  }, [testConfigQuery.data])

  // Per-problem execution availability (LG-247); null until config loads
  const availableLanguages: readonly string[] | null =
    testConfigQuery.data?.availableLanguages ?? null

  // Combine all data sources: local state + tRPC editorial + tRPC test config
  const mergedData: ProblemData | null = useMemo(() => {
    if (!data) return null
    return {
      ...data,
      editorial: editorialData,
      testConfig: dbTestConfig,
      testCases: dbTestCases,
      examples: dbExamples,
      topics: dbTopics,
    }
  }, [data, editorialData, dbTestConfig, dbTestCases, dbExamples, dbTopics])

  const sections = useMemo(
    () => ({
      statement: statementSection,
      editorial: editorialSection,
      testCases: testCasesSection,
    }),
    [statementSection, editorialSection, testCasesSection],
  )

  const value: ProblemContextValue = useMemo(() => {
    const firstError = [
      statementSection,
      editorialSection,
      testCasesSection,
    ].find(
      (s): s is Extract<SectionState, { status: 'error' }> =>
        s.status === 'error',
    )

    return {
      problemSlug,
      data: mergedData,
      availableLanguages,
      sections,
      loading: statementSection.status === 'loading',
      error: firstError?.message ?? null,
      statementLoading: statementSection.status === 'loading',
      editorialLoading: editorialSection.status === 'loading',
      testCasesLoading: testCasesSection.status === 'loading',
    }
  }, [
    problemSlug,
    mergedData,
    availableLanguages,
    sections,
    statementSection,
    editorialSection,
    testCasesSection,
  ])

  return <ProblemContext value={value}>{children}</ProblemContext>
}
